import { useState } from "react";
import type { ConvenioNovo } from "@/api/contrato";
import { useBancos, useConta, useCriarConvenio } from "@/api/consultas";
import { recursoInexistente } from "@/api/problema";
import { useAvisos } from "@/componentes/Avisos";
import { Campo } from "@/componentes/Campo";
import { Confirmacao } from "@/componentes/Confirmacao";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { useSessao } from "@/sessao/useSessao";
import { bancoDoCatalogo } from "./bancos";
import {
  camposNaTela,
  corpoDaCriacao,
  lerProximoNossoNumero,
  linhaDaConta,
  regrasDoBanco,
  useValoresDoConvenio,
  validarCriacao,
  valoresIniciais,
} from "./convenio";
import { EsqueletoDoFormulario, FormularioDeConvenio } from "./FormularioDeConvenio";
import { errosDoFormulario, textoDaRecusa } from "./recusas";

const DICA_DO_SEQUENCIAL =
  "Primeiro nosso número deste convênio. 1 em quem nunca emitiu. Quem já emitia por outro meio " +
  "continua a sequência dele: número repetido é título duplicado no banco. Não muda depois de criado.";

function textoDaFalhaDeLeitura(falhaDaConta: unknown, falhaDoCatalogo: unknown) {
  if (recursoInexistente(falhaDaConta)) return "Conta não encontrada.";
  const falha: unknown = falhaDaConta ?? falhaDoCatalogo;
  return falha === null ? undefined : textoDaRecusa(falha);
}

/*
 * O convênio novo: herda conta e banco da linha, sem seletor, e os campos saem do
 * catálogo do banco da conta.
 *
 * Quem o monta é a aba Contas, por `?abrir=novo-convenio&conta={id}`, e fechar desmonta:
 * reabrir vem com campos e recusa limpos, sem nada para zerar.
 *
 * Sem a conta ou sem o catálogo não há o que cadastrar, porque não se sabe o que o banco
 * usa: o modal fica aberto com o esqueleto e, na falha, o botão de envio tenta de novo.
 */
export function NovoConvenio({
  pessoaID,
  contaID,
  aoFechar,
}: {
  pessoaID: string;
  contaID: string;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const conta = useConta(cliente, pessoaID, contaID);
  const catalogo = useBancos(cliente, true);
  const criar = useCriarConvenio(cliente, pessoaID, contaID);

  const regras = regrasDoBanco(
    bancoDoCatalogo(catalogo.data?.bancos ?? [], conta.data?.banco ?? ""),
  );
  const { valores, erros, setErros, mudar } = useValoresDoConvenio(valoresIniciais(regras), regras);

  const [proximo, setProximo] = useState("1");
  const [erroDoProximo, setErroDoProximo] = useState<string | undefined>(undefined);
  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);
  const [aConfirmar, setAConfirmar] = useState<ConvenioNovo | null>(null);

  const pronto = conta.data !== undefined && catalogo.data !== undefined;
  const erroDeLeitura = pronto ? undefined : textoDaFalhaDeLeitura(conta.error, catalogo.error);

  async function gravar(corpo: ConvenioNovo) {
    try {
      await criar.mutateAsync(corpo);
    } catch (falha) {
      // A recusa volta para o formulário, e não para a confirmação: é lá que está o
      // campo que o operador precisa corrigir.
      const { porCampo, geral } = errosDoFormulario(falha, [
        ...camposNaTela(regras, valores.modo_registro),
        "proximo_nosso_numero",
      ]);
      const { proximo_nosso_numero: recusaDoProximo, ...dosCampos } = porCampo;
      setErros(dosCampos);
      setErroDoProximo(recusaDoProximo);
      setErroGeral(geral);
      setAConfirmar(null);
      return;
    }

    avisos.feito("Convênio cadastrado");
    aoFechar();
  }

  function enviar() {
    setErroGeral(undefined);

    const validacao = validarCriacao(valores, regras);
    const sequencial = lerProximoNossoNumero(proximo, regras);
    setErros(validacao.ok ? {} : validacao.erros);
    setErroDoProximo(sequencial.ok ? undefined : sequencial.erro);
    if (!validacao.ok || !sequencial.ok) return;

    const corpo = corpoDaCriacao(validacao.convenio, sequencial.numero);

    /*
     * Valor diferente de 1 é numeração continuada, e não tem volta: o sequencial nunca
     * mais muda. Digitar o número de novo é o que separa o que o operador decidiu de um
     * dígito a mais que passou na revisão.
     */
    if (sequencial.numero !== 1) {
      setAConfirmar(corpo);
      return;
    }
    void gravar(corpo);
  }

  function tentarDeNovo() {
    void conta.refetch();
    void catalogo.refetch();
  }

  return (
    <>
      <ModalDeCadastro
        aberto
        aoFechar={aoFechar}
        titulo="Novo convênio"
        {...(conta.data !== undefined && { descricao: linhaDaConta(conta.data) })}
        aoEnviar={pronto ? enviar : tentarDeNovo}
        enviando={criar.isPending || (!pronto && erroDeLeitura === undefined)}
        erro={erroDeLeitura ?? erroGeral}
        rotuloDeEnvio={erroDeLeitura === undefined ? "Cadastrar" : "Tentar de novo"}
      >
        {pronto ? (
          <>
            <FormularioDeConvenio valores={valores} erros={erros} regras={regras} aoMudar={mudar} />

            <Campo
              rotulo="Próximo nosso número"
              dica={DICA_DO_SEQUENCIAL}
              value={proximo}
              onChange={(evento) => {
                setProximo(evento.target.value);
                setErroDoProximo(undefined);
              }}
              erro={erroDoProximo}
              aria-required
              inputMode="numeric"
              autoComplete="off"
              className="dados w-40"
            />
          </>
        ) : (
          erroDeLeitura === undefined && <EsqueletoDoFormulario />
        )}
      </ModalDeCadastro>

      <Confirmacao
        pedido={
          aConfirmar === null
            ? null
            : {
                severidade: "critica",
                identidade: String(aConfirmar.proximo_nosso_numero),
                titulo: `Começar a numeração em ${aConfirmar.proximo_nosso_numero}?`,
                corpo: "O sequencial não muda depois de criado.",
                verbo: "Cadastrar convênio",
                digitarParaLiberar: String(aConfirmar.proximo_nosso_numero),
              }
        }
        aoCancelar={() => setAConfirmar(null)}
        aoConfirmar={async () => {
          if (aConfirmar !== null) await gravar(aConfirmar);
        }}
      />
    </>
  );
}
